import { useContext, useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReferralContext } from '@contexts/referral-context';
import { ToastType, useToast } from '@pulse-web-ui/toast';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import { useConfirmAgent } from '@entities/agent-user';
import { Otp, OtpType, useHandleOtpCodeErrors, useOtp } from '@entities/otp';
import { IncompleteRegistration } from '@entities/registration';

import { AnalyticEvent } from '@shared/constants';
import {
  BackButton,
  CANDIDATE_AUTH,
  OTP_INPUTS_NUM,
  phoneDisplayValueCasting,
  useStores,
} from '@shared/index';

import { useCandidateRegistrationSearchParams } from '../../hooks';
import {
  CandidateRegistrationStep,
  CandidateRegistrationTab,
} from '../../model';

export const OtpForm = observer(() => {
  const { t } = useTranslation();
  const {
    MainStore: {
      agentUserStore: { newCandidate, confirmToken },
      otpCooldownStore: { startCooldown, clear },
    },
  } = useStores();
  const navigate = useNavigate();
  const { referralData, clearReferralData } = useContext(ReferralContext);
  const { refMagCode, refDeptCode, refDeptId, refBranchId, linkId } =
    referralData;
  const { createReferralSearchParams } = useCandidateRegistrationSearchParams();
  const addToast = useToast();
  const { code, inputError, handleCodeChange, handleError, helperTextError } =
    useOtp(startCooldown, OtpType.CANDIDATE_REGISTRATION);
  const [isFailedScreening, setIsFailedScreening] = useState(false);

  const { isFetching, res, refetch, error } = useConfirmAgent(
    {
      token: confirmToken || '',
      login: newCandidate?.phone || '',
      code,
      isCandidate: true,
      ...(linkId && { linkId }),
    },
    [newCandidate?.phone, confirmToken, code, linkId]
  );

  const handleGoBack = () => {
    const { search } = createReferralSearchParams({
      tab: CandidateRegistrationTab.REGISTRATION,
      step: CandidateRegistrationStep.CREATE_PASSWORD,
      refDeptCode,
      refMagCode,
      refDeptId,
      refBranchId,
      linkId,
    });

    navigate({ pathname: CANDIDATE_AUTH, search });
  };

  useEffect(() => {
    clear(OtpType.CANDIDATE_REGISTRATION);
  }, [clear]);

  useEffect(() => {
    sendAnalyticEvent(AnalyticEvent.CANDIDATE_REG_PASSWORD_CONFIRM);
  }, []);

  useEffect(() => {
    if (code.length === OTP_INPUTS_NUM) {
      refetch();
    }
  }, [code]);

  useEffect(() => {
    if (!isFetching && res) {
      if (res.isActive) {
        sendAnalyticEvent(AnalyticEvent.CANDIDATE_REG_PASSWORD_SUCCESS);
        clearReferralData();
        navigate(CANDIDATE_AUTH);
        addToast({
          title: t('CANDIDATE_REGISTRATION:toast.title'),
          description: t('CANDIDATE_REGISTRATION:toast.description'),
          type: ToastType.SUCCESS,
        });
      } else {
        setIsFailedScreening(true);
      }
    }
  }, [isFetching, res, addToast, clearReferralData]);

  useHandleOtpCodeErrors({
    error,
    handleError,
    analyticEvent: AnalyticEvent.CANDIDATE_REG_PASSWORD_ERROR,
  });

  if (isFailedScreening) {
    return <IncompleteRegistration authRoute={CANDIDATE_AUTH} />;
  }

  return (
    <>
      <BackButton
        goBack={handleGoBack}
        testId='candidate-password'
      />
      <Otp
        otpType={OtpType.CANDIDATE_REGISTRATION}
        isWithContainer={false}
        title={t('CANDIDATE_OTP:header')}
        code={code}
        onCodeChange={handleCodeChange}
        onErrorResend={handleError}
        hasError={!!inputError}
        errorText={inputError}
        helperTextError={helperTextError}
        disabled={isFetching}
        phone={newCandidate?.phone}
        subTitle={
          <Trans
            values={{
              phoneNumber:
                newCandidate?.phone &&
                phoneDisplayValueCasting(newCandidate?.phone, true),
            }}
          >
            {t('CANDIDATE_OTP:subTitle')}
          </Trans>
        }
      />
    </>
  );
});
